/** biome-ignore-all lint/correctness/useExhaustiveDependencies: it's ok */
import Caption from "@components/controls/Caption";
import Path from "@core/FileProvider/Path/Path";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import useWatch from "@core-ui/hooks/useWatch";
import toggleSignature from "@core-ui/toggleSignature";
import { useNodeViewContext } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import WrapInGalleryButton from "@ext/markdown/elements/gallery/edit/components/WrapInGalleryButton";
import ImageActions from "@ext/markdown/elements/image/edit/components/ImageActions";
import ImageEditor from "@ext/markdown/elements/image/edit/components/ImageEditor";
import ImageToInlineButton from "@ext/markdown/elements/image/edit/components/ImageToInlineButton";
import type { Crop, ImageObject } from "@ext/markdown/elements/image/edit/model/imageEditorTypes";
import ImageRenderer from "@ext/markdown/elements/image/render/components/ImageRenderer";
import { getBlobFromBuffer } from "@ext/markdown/elements/image/render/logic/cropImage";
import type { Editor } from "@tiptap/core";
import { type ReactElement, type RefObject, useCallback, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

interface ImageDataProps {
	editor: Editor;
	id: string;
	title: string;
	objects: ImageObject[];
	src: string;
	alt: string;
	width: string;
	height: string;
	crop: Crop;
	scale: number;
	selected: boolean;
	hoverElementRef: RefObject<HTMLDivElement>;
	commentId?: string;
	getPos: () => number;
	// biome-ignore lint/suspicious/noExplicitAny: it's ok
	updateAttributes?: (attributes: Record<string, any>) => void;
	className?: string;
}

const Image = (props: ImageDataProps): ReactElement => {
	const {
		editor,
		getPos,

		updateAttributes,
		selected,
		id,
		title,
		objects,
		src,
		alt,
		width,
		height,
		crop,
		commentId,
		scale,
	} = props;
	const resourceService = ResourceService.value;

	const isEditable = editor.isEditable;

	const signatureRef = useRef<HTMLInputElement>(null);
	const [hasSignature, setHasSignature] = useState(isEditable && title?.length > 0);
	const nodeViewProps = useNodeViewContext();
	const isGif = new Path(src).extension === "gif";
	const showResizer = selected && isEditable;

	useWatch(() => {
		if (!hasSignature && title?.length) return setHasSignature(true);
	}, [title]);

	const focusToSignature = useCallback(() => {
		if (!hasSignature) signatureRef.current?.focus();
	}, [hasSignature]);

	const addSignature = useCallback(() => {
		setHasSignature((prev) => toggleSignature(prev, signatureRef.current, updateAttributes));
		focusToSignature();
	}, [signatureRef, updateAttributes, focusToSignature]);

	const handleEdit = useCallback(() => {
		const handleSave = (objects: ImageObject[], crop: Crop) => {
			updateAttributes({ crop, objects });
		};

		const element = document.body.appendChild(document.createElement("div"));
		element.setAttribute("id", "image-editor");

		const buffer = resourceService.getBuffer(src);
		const blob = getBlobFromBuffer(buffer);
		const newSrc = URL.createObjectURL(blob);

		const root = createRoot(element);
		const handleToggle = () => {
			root.unmount();
			URL.revokeObjectURL(newSrc);
			element.remove();
		};

		root.render(
			<ImageEditor
				crop={crop ?? { x: 0, y: 0, w: 100, h: 100 }}
				handleSave={handleSave}
				handleToggle={handleToggle}
				objects={objects ?? []}
				src={newSrc}
			/>,
		);
	}, [updateAttributes, crop, objects, resourceService.data, src]);

	const onLoseFocus = useCallback(
		(e) => {
			const target = e.target as HTMLInputElement;
			if (target.value.length || hasSignature) return;

			updateAttributes({ title: "" });
			return setHasSignature(false);
		},
		[updateAttributes, hasSignature],
	);

	const onUpdate = useCallback((text) => updateAttributes({ title: text }), [updateAttributes]);

	useBlockToolbar(
		nodeViewProps,
		<>
			<ImageActions
				addSignature={addSignature}
				caption={title}
				handleEdit={handleEdit}
				hasSignature={hasSignature}
				isGif={isGif}
			/>
			<ImageToInlineButton editor={editor} getPos={getPos} />
			<WrapInGalleryButton editor={editor} getPos={getPos} />
		</>,
		{ comment: true, float: true },
	);

	return (
		<>
			<ImageRenderer
				alt={alt}
				commentId={commentId}
				crop={crop}
				height={height}
				id={id}
				noEm
				objects={objects}
				openEditor={handleEdit}
				realSrc={src}
				scale={scale}
				showResizer={showResizer}
				src={src}
				title={title}
				updateAttributes={updateAttributes}
				width={width}
			/>
			<Caption
				editor={editor}
				getPos={getPos}
				onLoseFocus={onLoseFocus}
				onUpdate={onUpdate}
				ref={signatureRef}
				text={title}
				visible={hasSignature}
			/>
		</>
	);
};

export default Image;
